import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IconPlus, IconChevronRight } from '@tabler/icons-react'
import {
  Button,
  BUTTON_PREVIEW_STATE_LABELS,
  BUTTON_PREVIEW_STATES,
  BUTTON_SHOWCASE_VARIANT_GROUPS,
  BUTTON_SIZE_LABELS,
  BUTTON_SIZES,
  type ButtonSize,
} from '@components/ui'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'

const ICON_SIZE: Record<ButtonSize, number> = {
  xs: 14,
  sm: 14,
  md: 16,
  lg: 20,
}

const ButtonShowcase: React.FC = () => {
  const [size, setSize] = useState<ButtonSize>('md')
  const [dark, setDark] = useState(false)
  const [label, setLabel] = useState('Button')
  const [disabled, setDisabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showIcons, setShowIcons] = useState(false)
  const [fullWidth, setFullWidth] = useState(false)
  const [highlightedGroupId, setHighlightedGroupId] = useState('primary')
  const liveRowRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    liveRowRefs.current[highlightedGroupId]?.scrollIntoView({
      block: 'nearest',
      behavior: 'smooth',
    })
  }, [highlightedGroupId])

  const iconSize = ICON_SIZE[size]
  const leftIcon = showIcons ? (
    <IconPlus size={iconSize} stroke={2} />
  ) : undefined
  const rightIcon = showIcons ? (
    <IconChevronRight size={iconSize} stroke={2} />
  ) : undefined

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'button-highlight-group',
            label: 'Highlighted variant',
            control: (
              <select
                id="button-highlight-group"
                className="ui-field-control"
                value={highlightedGroupId}
                onChange={(event) => setHighlightedGroupId(event.target.value)}
              >
                {BUTTON_SHOWCASE_VARIANT_GROUPS.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.label}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'button-size',
            label: 'Size',
            control: (
              <select
                id="button-size"
                className="ui-field-control"
                value={size}
                onChange={(event) => setSize(event.target.value as ButtonSize)}
              >
                {BUTTON_SIZES.map((option) => (
                  <option key={option} value={option}>
                    {BUTTON_SIZE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
          {
            id: 'button-dark',
            label: 'Dark mode',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="button-dark"
                  type="checkbox"
                  checked={dark}
                  onChange={(event) => setDark(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'button-full-width',
            label: 'Full width',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="button-full-width"
                  type="checkbox"
                  checked={fullWidth}
                  onChange={(event) => setFullWidth(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'button-icons',
            label: 'Icons',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="button-icons"
                  type="checkbox"
                  checked={showIcons}
                  onChange={(event) => setShowIcons(event.target.checked)}
                />
                <span>Left + right</span>
              </label>
            ),
          },
        ],
      },
      {
        title: 'State',
        fields: [
          {
            id: 'button-label',
            label: 'Label',
            control: (
              <input
                id="button-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
          {
            id: 'button-disabled',
            label: 'Disabled',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="button-disabled"
                  type="checkbox"
                  checked={disabled}
                  onChange={(event) => setDisabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
          {
            id: 'button-loading',
            label: 'Loading',
            control: (
              <label className="ui-field-checkbox">
                <input
                  id="button-loading"
                  type="checkbox"
                  checked={loading}
                  onChange={(event) => setLoading(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
            ),
          },
        ],
      },
    ],
    [
      size,
      dark,
      label,
      disabled,
      loading,
      showIcons,
      fullWidth,
      highlightedGroupId,
    ],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Button"
        description="Primary, secondary, tertiary, ghost, and text hierarchies with sizes XS–L and full interactive states."
        stageClassName="ui-preview__stage--wide button-showcase__stage-wrap"
      >
        <div
          className={`button-showcase__sheet${
            dark ? ' button-showcase__sheet--dark' : ''
          }`}
        >
          <section
            className={`ui-showcase-live${
              dark ? ' ui-showcase-live--dark' : ''
            }`}
          >
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              All variants at the selected size — labels above each button for
              quick comparison. Hover and click to try states.
            </p>
            <div className="button-showcase__live-grid">
              {BUTTON_SHOWCASE_VARIANT_GROUPS.map((group) => (
                <div
                  key={group.id}
                  ref={(element) => {
                    liveRowRefs.current[group.id] = element
                  }}
                  className={`button-showcase__live-card${
                    group.id === highlightedGroupId
                      ? ' button-showcase__live-card--selected'
                      : ''
                  }`}
                >
                  <span className="button-showcase__live-card-label">
                    {group.label}
                  </span>
                  <Button
                    variant={group.variant}
                    size={size}
                    destructive={group.destructive}
                    darkMode={dark}
                    label={label}
                    disabled={disabled}
                    loading={loading}
                    fullWidth={fullWidth}
                    leftIcon={leftIcon}
                    rightIcon={rightIcon}
                  />
                </div>
              ))}
            </div>
          </section>

          {BUTTON_SHOWCASE_VARIANT_GROUPS.map((group) => (
            <section
              key={group.id}
              className={`button-showcase__group${
                group.id === highlightedGroupId
                  ? ' button-showcase__group--selected'
                  : ''
              }`}
            >
              <h3 className="button-showcase__section-title">{group.label}</h3>

              <div className="button-showcase__matrix-header">
                <span className="button-showcase__matrix-corner">State</span>
                {BUTTON_SIZES.map((sizeOption) => (
                  <span
                    key={sizeOption}
                    className="button-showcase__matrix-header-cell"
                  >
                    {BUTTON_SIZE_LABELS[sizeOption]}
                  </span>
                ))}
              </div>

              {BUTTON_PREVIEW_STATES.map((state) => (
                <div key={state} className="button-showcase__matrix-row">
                  <span className="button-showcase__matrix-row-label">
                    {BUTTON_PREVIEW_STATE_LABELS[state]}
                  </span>
                  {BUTTON_SIZES.map((sizeOption) => (
                    <div
                      key={`${group.id}-${state}-${sizeOption}`}
                      className="button-showcase__matrix-cell"
                    >
                      <Button
                        variant={group.variant}
                        size={sizeOption}
                        destructive={group.destructive}
                        darkMode={dark}
                        label={label}
                        previewState={state}
                        fullWidth={fullWidth}
                        leftIcon={
                          showIcons ? (
                            <IconPlus size={ICON_SIZE[sizeOption]} stroke={2} />
                          ) : undefined
                        }
                        rightIcon={
                          showIcons ? (
                            <IconChevronRight
                              size={ICON_SIZE[sizeOption]}
                              stroke={2}
                            />
                          ) : undefined
                        }
                      />
                    </div>
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      </UiPreviewCanvas>

      <UiPropertiesPanel componentName="Button" sections={propertySections} />
    </>
  )
}

export default ButtonShowcase
